import React, { createContext, useContext, useId } from 'react';
import { ActionAccessContext } from './ActionAccessContext';
import { canPageAction } from '../../services/accessPolicy';
import { canPermission, canVisitPage, pagePolicy } from '../../services/pagePolicies';
import { controlPermission } from '../../services/controlPolicies';

const FormActionContext = createContext(null);

export function allowedControl(access, decision, props, formDecision) {
  if (!access) return true; // Authentication/platform shells use their own boundaries.
  const { user, pathname } = access;
  if (user?.is_owner || user?.is_super_admin) return true;
  const permission = props['data-access-permission'] || decision.permission || controlPermission(pathname, props['data-testid']);
  if (permission) return canPermission(user, permission);
  let action = props['data-access-action'] || decision.action;
  if (action === 'submit') {
    const resolved = formDecision || { action: pagePolicy(pathname)?.action === 'edit' ? 'edit' : 'create' };
    return allowedControl(access, resolved, {}, null);
  }
  if (action === 'navigate') {
    const target = props.to || props.href || decision.destination;
    if (target && !String(target).startsWith('/')) return true;
    if (target) return canVisitPage(user, target.replace(/\$\{[^}]+\}/g, 'record'));
    return true; // Dynamic read-only navigation is checked again at destination.
  }
  if (action === 'local' || action === 'explicit') return true;
  if (action === 'explicit-form') action = 'write';
  if (action === 'write' && ['create', 'edit'].includes(pagePolicy(pathname)?.action)) action = pagePolicy(pathname).action;
  if (action === 'export' && canPermission(user, 'reports.export.export')) return true;
  return canPageAction(user, pathname, action, props['data-access-resource'] || decision.resource);
}

export const ScopedControl = React.forwardRef(function ScopedControl({ as: Component = 'button', policy, navigationTarget, children, ...props }, ref) {
  const access = useContext(ActionAccessContext);
  const formDecision = useContext(FormActionContext);
  const uid = useId().replace(/:/g, '');
  const decision = policy || { action: 'UNREVIEWED' };
  if (!allowedControl(access, decision, { ...props, ...(typeof navigationTarget === 'string' ? { to: navigationTarget } : {}) }, formDecision)) return null;
  const testid = props['data-testid'] || `${policy?.testId || 'control'}-${uid}`;
  const control = <Component {...props} ref={ref} data-testid={testid} data-control-policy={decision.action || decision.permission}>
    {children}
  </Component>;
  return Component === 'form' ? <FormActionContext.Provider value={{ ...decision,
    ...(props['data-access-permission'] ? { permission: props['data-access-permission'] } : {}),
    ...(props['data-access-action'] ? { action: props['data-access-action'] } : {}) }}>{control}</FormActionContext.Provider> : control;
});